/**
 * useModulePaneEscapeClose — Esc closes the open module pane and refocuses chat
 * (issue #3938).
 *
 * When a module pane is open, pressing Escape drops the pane back to chat-only
 * and returns focus to the chat input — but ONLY in the unambiguously safe case.
 * The handler declines (no-op, pane stays) whenever something else is already
 * consuming Escape, so it can NEVER bypass an unsaved-changes guard:
 *   (a) the event was already `defaultPrevented` — a modal's `useEscapeKey` (or a
 *       manual handler) ran and called preventDefault();
 *   (b) any modal/dialog overlay is mounted in the DOM (belt-and-suspenders for
 *       window-listener ordering); or
 *   (c) the active element is an editable inner editor (input / textarea /
 *       contentEditable) other than the chat input — so inline note/doc editors
 *       exit first and the pane survives this keypress.
 *
 * This is the DECLINE half of the escape-layer contract (apps/web/AGENTS.md →
 * "Escape-layer contract"). The other half lives in the modals: every dirty
 * editor routes its OWN Escape through its unsaved-changes guard (ContactEditModal
 * / TaskEditModal / EventEditModal confirmation dialogs, ComposeEmailModal draft
 * stash, DocEditorModal autosave/save-failure buffer) and preventDefaults, and
 * inline editors (NoteEditor textarea) consume Escape to blur first. So the first
 * Esc dismisses the inner layer (saving or confirming as appropriate) and a
 * subsequent Esc — with nothing dirty/open — closes the pane. One layer per
 * keypress; no dirty edit is ever silently discarded.
 *
 * Closing reuses the EXISTING rail/header close path (conversationEvents.
 * closeModulePane → useChatModulePaneActions.hideModulePane), so pane state stays
 * the single source of truth (ADR 028).
 */
import { useEffect } from 'react';
import type { RefObject } from 'react';
import { dispatchWindowEvent, conversationEvents } from '@/lib/conversation-events';

// Overlays that own their own Escape handling. We intentionally match BROADLY
// and CONSERVATIVELY: if any plausible overlay/dialog is mounted we decline so
// it (not us) handles Escape. Closing the pane out from under an open modal is a
// data-loss path (e.g. TaskEditModal's unsaved-note guard), so a false decline
// — the user presses Esc again once the overlay is gone — is far cheaper than a
// false close.
//
// Detection covers, in priority order:
//   1. ARIA dialog semantics — `role="dialog"` / `aria-modal="true"`.
//   2. The repo-wide overlay/backdrop class convention (escape-layer contract,
//      apps/web/AGENTS.md). Every modal/menu/flyout backdrop in apps/web names
//      its root element `*-overlay` or `*-backdrop`
//      (team-modal-overlay, integrations-modal-overlay, profile-modal-overlay,
//      add-workspace-modal-overlay, task-modal-overlay, event-modal-overlay,
//      note-editor-modal-overlay, compose-email-modal-overlay,
//      unsaved-changes-modal-overlay, archived-overlay, interview-overlay, …).
//      The attribute selectors below match a `-overlay`/`-backdrop` class token
//      whether it is the sole/last token (`[class$="…"]`) or followed by
//      modifier tokens such as `mobile` (`[class*="… "]`).
//
// As of issue #3938 the previously classless overlays were brought into the
// convention: ComposeEmailModal's inline-style overlay now carries
// `compose-email-modal-overlay` + role="dialog", and the shared
// UnsavedChangesGuardModal carries `unsaved-changes-modal-overlay`. So a dirty
// modal's Esc is caught BOTH by its own preventDefault (clause (a)) AND by the
// DOM-presence guard (clause (b)) — defence in depth against listener ordering.
const MODAL_OVERLAY_SELECTOR = [
  '[role="dialog"]',
  '[aria-modal="true"]',
  '[class$="-overlay"]',
  '[class*="-overlay "]',
  '[class$="-backdrop"]',
  '[class*="-backdrop "]',
].join(', ');

export interface UseModulePaneEscapeCloseDeps {
  /** Whether a module pane is currently open. The listener is only registered while true. */
  isModulePaneVisible: boolean;
  /** Chat composer textarea ref — refocused after the pane closes. */
  chatInputRef: RefObject<HTMLTextAreaElement | null>;
}

export function useModulePaneEscapeClose({
  isModulePaneVisible,
  chatInputRef,
}: UseModulePaneEscapeCloseDeps): void {
  useEffect(() => {
    if (!isModulePaneVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A modal/editor already consumed this Escape — let it own the keypress.
      if (e.defaultPrevented) return;
      // Belt-and-suspenders for window-listener ordering: if any modal overlay
      // is mounted, decline so it (not us) handles Escape.
      if (document.querySelector(MODAL_OVERLAY_SELECTOR)) return;

      const activeElement = document.activeElement as HTMLElement | null;
      const isEditable =
        !!activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable);
      const isChatInput = activeElement === chatInputRef.current;
      // An inner note/doc editor has focus — it exits first; the pane stays. The
      // chat input itself is allowed (the user is in chat, not an inner editor).
      if (isEditable && !isChatInput) return;

      e.preventDefault();
      // Reuse the existing rail/header close path + documented refocus.
      dispatchWindowEvent(conversationEvents.closeModulePane);
      chatInputRef.current?.focus();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModulePaneVisible, chatInputRef]);
}
